import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../services/api';
import { fmtMoney, fmtHours, fmtNumber } from './format';
import { c, tbl } from '../../theme';

// ── Who carries the overtime ─────────────────────────────────────────────────
//
// The board answered "how much" and "where"; this answers "who" and "whose".
// It is deliberately NOT a chronic-versus-one-off split: on prod payroll nearly
// every overtime dollar belongs to someone with overtime almost every week, so
// that split puts the whole company in one bucket. What separates people is
// how much -- average weekly OT hours across the span -- so the bands lead,
// and a band selects the names underneath.
//
// The manager table is the owner column the board did not have. It ranks by
// how many people a manager has at the high line, not by dollars, because the
// dollars largely follow headcount and would just rank the biggest yards first.
//
// Names follow the same gate as the cost-center drill-down: the server returns
// none to a read-only role or for a cost center held without employee access,
// and this panel says so rather than showing an empty table.

const MANAGER_ROWS = 12;
const PEOPLE_ROWS  = 50;

export default function IntensityPanel({ scopeParams, filterQ = '', onSelectEmployee }) {
  const qs = scopeParams ? `${scopeParams}${filterQ}` : null;
  const { data, isLoading, error } = useQuery({
    queryKey: ['ot-intensity', qs],
    queryFn:  () => api.get(`/overtime/intensity?${qs}`),
    enabled:  !!qs,
  });

  // Opens on the heaviest band: the people a manager would act on first.
  const [band, setBand]         = useState('20+');
  const [manager, setManager]   = useState(null);
  const [allManagers, setAllManagers] = useState(false);
  const [morePeople, setMorePeople]   = useState(false);

  const runs      = data?.run_count || 0;
  const bands     = data?.bands || [];
  const managers  = data?.by_manager || [];
  const people    = data?.people || [];
  const high      = data?.high_threshold ?? 15;

  const shown = useMemo(
    () => (data?.people || []).filter((p) => (!band || p.band === band) && (!manager || p.manager === manager)),
    [data, band, manager],
  );

  const pickBand = (key) => { setBand((cur) => (cur === key ? null : key)); setMorePeople(false); };
  const pickManager = (m) => { setManager((cur) => (cur === m ? null : m)); setMorePeople(false); };

  return (
    <>
      <div style={s.head}>
        <div style={s.title}>Who carries the overtime</div>
        {runs > 0 && (
          <div style={s.sub}>
            Average weekly OT hours per person over the {runs} pay run{runs === 1 ? '' : 's'} in view
          </div>
        )}
      </div>

      {isLoading ? (
        <div style={s.card}><div style={s.empty}>Loading…</div></div>
      ) : error ? (
        <div style={s.card}><div style={s.empty}>Could not load this panel.</div></div>
      ) : !runs ? (
        <div style={s.card}><div style={s.empty}>No overtime in this selection.</div></div>
      ) : (
        <>
          {/* Bands as tiles, not a chart: five numbers a reader compares and
              clicks, where a bar would add a mark and no reading. */}
          <div style={s.bands}>
            {bands.map((b) => (
              <button key={b.key} type="button" onClick={() => pickBand(b.key)}
                      aria-pressed={band === b.key}
                      style={{ ...s.band, ...(band === b.key ? s.bandOn : null) }}>
                <div style={s.bandLabel}>{b.label}</div>
                <div style={s.bandValue}>{fmtNumber(b.employees)}</div>
                <div style={s.bandMeta}>people · {fmtMoney(b.ot_amount)} · {b.share_amount}% of cost</div>
              </button>
            ))}
          </div>

          <div style={s.cols}>
            <div style={s.col}>
              <div style={s.colTitle}>By manager</div>
              <div style={s.card}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.th}>Manager</th>
                        <th style={s.thR}>People</th>
                        <th style={s.thR} title={`Averaging ${high} or more OT hours a week`}>{high}+ hrs/wk</th>
                        <th style={s.thR}>OT cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(allManagers ? managers : managers.slice(0, MANAGER_ROWS)).map((m) => (
                        <tr key={m.manager} onClick={() => pickManager(m.manager)}
                            style={{ ...s.tr, ...(manager === m.manager ? s.trOn : null) }}>
                          <td style={s.td}>{m.manager}</td>
                          <td style={s.tdR}>{fmtNumber(m.employees)}</td>
                          <td style={s.tdRb}>{fmtNumber(m.high)}</td>
                          <td style={s.tdR}>{fmtMoney(m.ot_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {managers.length > MANAGER_ROWS && (
                <button type="button" style={s.more} onClick={() => setAllManagers((v) => !v)}>
                  {allManagers ? 'Show fewer' : `Show all ${managers.length} managers`}
                </button>
              )}
            </div>

            <div style={s.colWide}>
              <div style={s.colTitle}>
                People{band ? ` · ${bands.find((b) => b.key === band)?.label}` : ''}
                {manager ? ` · ${manager}` : ''}
                {(band || manager) && (
                  <button type="button" style={s.clear} onClick={() => { setBand(null); setManager(null); }}>
                    clear
                  </button>
                )}
              </div>
              {!data.can_view_employees ? (
                <div style={s.note}>
                  Names are not shown for your access. The bands and the manager table above cover
                  everyone in the cost centers you can see.
                </div>
              ) : (
                <>
                  <div style={s.card}>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={s.table}>
                        <thead>
                          <tr>
                            <th style={s.th}>Name</th>
                            <th style={s.th}>Cost center</th>
                            <th style={s.th}>Manager</th>
                            <th style={s.thR}>Avg hrs/wk</th>
                            <th style={s.thR}>Weeks with OT</th>
                            <th style={s.thR}>OT cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(morePeople ? shown : shown.slice(0, PEOPLE_ROWS)).map((p) => (
                            <tr key={p.employee_number}
                                onClick={onSelectEmployee ? () => onSelectEmployee(p.employee_number) : undefined}
                                style={{ ...s.tr, cursor: onSelectEmployee ? 'pointer' : 'default' }}>
                              <td style={s.td}>
                                {p.employee_name || p.employee_number}
                                {p.job_profile && <div style={s.subLine}>{p.job_profile}</div>}
                              </td>
                              <td style={s.td}>{p.cc_code}{p.cc_name ? ` · ${p.cc_name}` : ''}</td>
                              <td style={s.td}>{p.manager}</td>
                              <td style={s.tdRb}>{fmtHours(p.avg_weekly_hours)}</td>
                              <td style={s.tdR}>{p.weeks_with_ot} of {runs}</td>
                              <td style={s.tdR}>{fmtMoney(p.ot_amount)}</td>
                            </tr>
                          ))}
                          {/* "Nobody" only when it is true. If the band holds
                              people the list was cut before reaching, say so --
                              an empty list under a tile reading 628 is a
                              contradiction the reader cannot resolve. */}
                          {!shown.length && (
                            <tr><td colSpan={6} style={s.empty}>
                              {data.people_total > people.length
                                ? 'These people are beyond the listed set; the band totals above include them.'
                                : 'Nobody in this selection.'}
                            </td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {shown.length > PEOPLE_ROWS && (
                    <button type="button" style={s.more} onClick={() => setMorePeople((v) => !v)}>
                      {morePeople ? 'Show fewer' : `Show all ${fmtNumber(shown.length)}`}
                    </button>
                  )}
                  {/* The list is capped server-side, heaviest first, so a cut
                      that reaches past the cap is said to be partial. */}
                  {data.people_total > people.length && (
                    <div style={s.note}>
                      Listing the {fmtNumber(people.length)} heaviest of {fmtNumber(data.people_total)} people.
                      The bands and manager counts include everyone.
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}

const s = {
  head:      { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
               gap: '12px', margin: '26px 0 10px', flexWrap: 'wrap' },
  title:     { fontSize: '12px', fontWeight: 700, textTransform: 'uppercase',
               letterSpacing: '0.5px', color: c.textMuted },
  sub:       { fontSize: '12px', color: c.textMuted },
  bands:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
               gap: '10px', marginBottom: '14px' },
  band:      { appearance: 'none', textAlign: 'left', fontFamily: 'inherit', cursor: 'pointer',
               background: c.surface, borderWidth: '1px', borderStyle: 'solid', borderColor: c.border,
               borderRadius: '10px',
               padding: '10px 12px' },
  bandOn:    { borderColor: c.navy, boxShadow: `inset 0 0 0 1px ${c.navy}` },
  bandLabel: { fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px',
               color: c.textMuted },
  bandValue: { fontSize: '20px', fontWeight: 700, color: c.textStrong, marginTop: '2px',
               fontVariantNumeric: 'tabular-nums' },
  bandMeta:  { fontSize: '11px', color: c.textMuted, marginTop: '2px' },
  // Flex rather than an auto-fit grid: the people list wants twice the width
  // of the manager list, and a grid span of 2 overflows once only one column
  // fits. Wrapping drops the people list under the managers instead.
  cols:      { display: 'flex', flexWrap: 'wrap', gap: '18px', alignItems: 'flex-start' },
  col:       { minWidth: 0, flex: '1 1 300px' },
  colWide:   { minWidth: 0, flex: '2 1 460px' },
  colTitle:  { fontSize: '12px', fontWeight: 600, color: c.textStrong, marginBottom: '6px',
               display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  card:      { background: c.surface, border: `1px solid ${c.border}`, borderRadius: '12px', overflow: 'hidden' },
  table:     { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th:        { ...tbl.th },
  thR:       { ...tbl.thR, whiteSpace: 'nowrap' },
  tr:        { borderBottom: `1px solid ${c.canvas}`, cursor: 'pointer' },
  trOn:      { background: c.infoTint },
  td:        { padding: '8px 14px', color: c.text, verticalAlign: 'top' },
  tdR:       { padding: '8px 14px', color: c.text, textAlign: 'right', whiteSpace: 'nowrap',
               fontVariantNumeric: 'tabular-nums', verticalAlign: 'top' },
  tdRb:      { padding: '8px 14px', color: c.text, textAlign: 'right', whiteSpace: 'nowrap',
               fontVariantNumeric: 'tabular-nums', fontWeight: 600, verticalAlign: 'top' },
  subLine:   { fontSize: '11px', color: c.textFaint, marginTop: '1px' },
  more:      { appearance: 'none', border: 'none', background: 'transparent', color: c.info,
               fontSize: '12px', fontFamily: 'inherit', cursor: 'pointer', padding: '6px 2px' },
  clear:     { appearance: 'none', border: 'none', background: 'transparent', color: c.info,
               fontSize: '11px', fontFamily: 'inherit', cursor: 'pointer', padding: 0 },
  note:      { background: c.surfaceSunk, border: `1px solid ${c.border}`, borderRadius: '8px',
               padding: '8px 12px', fontSize: '12px', color: c.textStrong, marginTop: '8px' },
  empty:     { textAlign: 'center', padding: '24px', color: c.textFaint },
};
